import React, { useState, useEffect } from 'react';
import { Button, Typography } from '@crisellevaldez/speculo-ui';
import { auth } from '../lib/firebase';
import { sendPasswordResetEmail } from 'firebase/auth';
import { useLocation, useNavigate } from 'react-router-dom';
import { FormField } from '../components/common/FormField';
import { Mail } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { clearCachedOrganization } from '../stores/useOrganizationStore';

const resetSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Invalid email format'),
});

type ResetFormData = z.infer<typeof resetSchema>;

const ResetPasswordPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<ResetFormData>({
    resolver: zodResolver(resetSchema),
  });

  // Sign out and reset state on mount
  useEffect(() => {
    const cleanup = async () => {
      sessionStorage.setItem('passwordResetFlow', 'true');
      clearCachedOrganization();
      await auth.signOut();
    };
    cleanup();
  }, []);

  // Get email from location state if provided
  useEffect(() => {
    const email = (location.state as { email?: string } | null)?.email;
    if (email && !emailSent) { // Only handle if not already sent
      setValue('email', email);
      handleResetPassword({ email });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, setValue, emailSent]);

  const handleResetPassword = async (data: ResetFormData) => {
    if (emailSent) return;
    try {
      setLoading(true);
      setError(null);
      const email = data.email.toLowerCase();

      sessionStorage.setItem('passwordResetFlow', 'true');
      clearCachedOrganization();
      await auth.signOut();

      // Send the reset email via Firebase directly (Google's own mail infra)
      // rather than our SendGrid sender. Gmail was blocking our SendGrid IPs
      // (550 5.7.28 — IP reputation), so reset links never arrived. Firebase
      // sends from noreply@<project>.firebaseapp.com and sidesteps that.
      // The reset landing (AuthProvider) already consumes mode=resetPassword+oobCode.
      await sendPasswordResetEmail(auth, email);
      setEmailSent(true);
    } catch (err) {
      // Don't reveal whether the account exists — match the prior API's generic response.
      if ((err as { code?: string })?.code === 'auth/user-not-found') {
        setEmailSent(true);
      } else {
        console.error('Error in reset flow:', err);
        setError('An error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Sign out before returning to login
  const handleReturnToLogin = async () => {
    try {
      // Clear all auth-related storage
      clearCachedOrganization();
      sessionStorage.removeItem('passwordResetFlow');
      
      // Sign out and wait for completion
      await auth.signOut();
      
      // Force a small delay to ensure state updates
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Navigate after cleanup
      navigate('/login', { replace: true });
    } catch (err) {
      console.error('Error signing out:', err);
      setError('Error signing out. Please try again.');
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen p-4 bg-background">
      <div className="flex justify-center mb-8">
        <img src="/speculo.ai-black.png" alt="Speculo AI" className="h-12 dark:hidden" />
        <img src="/speculo.ai-white.png" alt="Speculo AI" className="h-12 hidden dark:block" />
      </div>
      <div className="w-full max-w-md space-y-6 bg-card rounded-lg shadow-xl p-8 border border-border">
        <div className="text-center">
          <Typography variant="h1" className="text-3xl font-bold">
            Reset Password
          </Typography>
          {!emailSent ? (
            <p className="text-muted-foreground mt-2">
              Enter your email to receive a password reset link
            </p>
          ) : (
            <p className="text-muted-foreground mt-2">
              Check your email for instructions to reset your password
            </p>
          )}
        </div>

        {error && (
          <div className="p-4 text-sm text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/30 rounded-lg" role="alert">
            {error}
          </div>
        )}

        {!emailSent ? (
          <form onSubmit={handleSubmit(handleResetPassword)} className="space-y-6 mt-8">
            <FormField
              label=""
              type="email"
              error={errors.email?.message}
              registration={register('email')}
              placeholder="Enter your email"
              startIcon={<Mail className="text-muted-foreground w-5 h-5" />}
            />

            <Button
              type="submit"
              variant="primary"
              className="w-full"
              isLoading={loading}
              disabled={loading}
            >
              Send Reset Link
            </Button>
          </form>
        ) : (
          <div className="space-y-4">
            <p className="text-muted-foreground">
              We've sent you an email with instructions to reset your password.
              Please check your inbox and follow the link to continue.
            </p>
            <Button
              variant="outline"
              className="w-full"
              onClick={handleReturnToLogin}
            >
              Return to Login
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export { ResetPasswordPage };
export default ResetPasswordPage;
